import { BASELINE_EM, type LabelLayout, type LabelPage, MM_PER_PT, qrMatrix } from "@bt/domain";
import { useMemo } from "react";

/**
 * The first page (a roll's whole strip) at real size, from the layout the server prints with: the
 * QR codes module by module and the text where it will be. White like the paper, also in dark mode.
 */
export function LabelPreview({
  layout,
  urls,
  title,
}: {
  layout: LabelLayout;
  urls: readonly string[];
  title: string;
}) {
  const page = layout.pages[0];
  const codes = useMemo(() => (page ? modulesOf(page, layout, urls) : []), [page, layout, urls]);
  if (!page) return null;
  const { cell } = layout;
  const line = (top: number, fontSize: number) => top + fontSize * MM_PER_PT * BASELINE_EM;
  return (
    <svg
      role="img"
      aria-label={title}
      width={`${page.width}mm`}
      height={`${page.height}mm`}
      viewBox={`0 0 ${page.width} ${page.height}`}
      className="block shrink-0 bg-white shadow-sm ring-1 ring-black/10"
    >
      <path d={codes.join("")} fill="#000" />
      {page.labels.map((label) => (
        <g key={label.index} className="font-mono" fill="#000" textAnchor="middle">
          {label.codeText !== null && cell.codeTop !== null ? (
            <text
              x={label.x + cell.width / 2}
              y={label.y + line(cell.codeTop, cell.fontSize)}
              fontSize={cell.fontSize * MM_PER_PT}
            >
              {label.codeText}
            </text>
          ) : null}
          {label.partNumber && cell.partNumberTop !== null ? (
            <text
              x={label.x + cell.width / 2}
              y={label.y + line(cell.partNumberTop, label.partNumber.fontSize)}
              fontSize={label.partNumber.fontSize * MM_PER_PT}
            >
              {label.partNumber.text}
            </text>
          ) : null}
        </g>
      ))}
    </svg>
  );
}

/** Each label's dark modules as path commands, a row's neighbours merged into one rectangle. */
function modulesOf(page: LabelPage, layout: LabelLayout, urls: readonly string[]): string[] {
  const { cell } = layout;
  const size = cell.moduleSize;
  return page.labels.map((label) => {
    const url = urls[label.index];
    if (!url) return "";
    const left = label.x + cell.qrX;
    let d = "";
    qrMatrix(url).forEach((row, y) => {
      let run = -1;
      for (let x = 0; x <= row.length; x++) {
        const dark = x < row.length && row[x];
        if (dark && run < 0) run = x;
        if (!dark && run >= 0) {
          d += `M${left + run * size} ${label.y + y * size}h${(x - run) * size}v${size}h${-(x - run) * size}z`;
          run = -1;
        }
      }
    });
    return d;
  });
}
